import _ from "lodash";
import React, { useMemo, useCallback } from "react";
import { IconButton, SearchInput, IconDisclosure } from "@toddle-edu/ds-web";
import {
  AddOutlined,
  CustomChannelOutlined,
  FamilyChannelOutlined,
} from "@toddle-edu/ds-icons";
import classNames from "classnames";
import { useI18n } from "Hooks";
import classes from "./HeaderV2.scss";
import {
  MESSAGING_SECTIONS,
  MESSAGING_V2_SECTIONS_CONFIG,
  recordMessagingFilters,
  CREATE_CHANNEL_CATEGORY,
  canShowStaffAndStudentChatToStaff,
  isMessagingGlobalHeaderRevampEnable,
} from "MessagingV2/modules/Utils";
import {
  setChatFilters,
  getMessagingV2ReduxState,
  setCreateChannelDropdownOpen,
} from "MessagingV2/modules/MessagingModule";
import { connect } from "react-redux";
import UserTypeFilter from "../ThreadList/UserTypeFilter/UserTypeFilter";
import ChannelTypeFilter from "../ThreadList/ChannelTypeFilter";
import { StaffPermission } from "Utils/UserConditional";
import { Dropdown, DropdownMenu } from "@toddle-edu/ds-web";

const styles = {
  dropdownIconStyle: { alignSelf: "flex-start" },
  itemTitleContainerStyle: { width: 400 },
  initiateButtonStyle: { height: "32px", width: "32px" },
  createChannelIconDisclosureStyle: { height: "32px" },
};

const HeaderRightContainer = props => {
  const {
    selectedSection,
    createChannelDropdownOpen,
    setChatFilters,
    setCreateChannelDropdownOpen,
    selectedUserTypes,
    selectedChannelTypes,
    addNewChannel,
    isUserStaff,
    showInitiateChat,
    addNewChat,
    setSearchText,
    canCreateChannel,
  } = props;

  const selectedSectionConfig = _.get(
    MESSAGING_V2_SECTIONS_CONFIG,
    selectedSection
  );

  const { t } = useI18n(["common"]);
  const threadHeaderClass = classNames({
    ["heading-5	"]: true,
    [classes.threadHeader]: true,
  });

  const channelOptions = useMemo(
    () => [
      {
        key: CREATE_CHANNEL_CATEGORY.CUSTOM_CHANNEL,
        label: t("common:custom_channel"),
        subText: (
          <div className={classes.dropdownSubText}>
            {t("common:custom_channel_create_subtext")}
          </div>
        ),
        icon: (
          <div style={styles.dropdownIconStyle}>
            <CustomChannelOutlined variant={"subtle"} />
          </div>
        ),
      },
      {
        key: CREATE_CHANNEL_CATEGORY.FAMILY_CHANNEL,
        label: t("common:family_channel"),
        subText: (
          <div className={classes.dropdownSubText}>
            {t("family_channel_create_subtext")}
          </div>
        ),
        icon: (
          <div style={styles.dropdownIconStyle}>
            <FamilyChannelOutlined variant={"subtle"} />
          </div>
        ),
      },
    ],
    [t]
  );
  const setUserTypeFilters = selectedUserTypes => {
    setChatFilters({
      chatType: MESSAGING_SECTIONS.CHAT,
      filters: { selectedUserTypes },
    });
    recordMessagingFilters({
      value: selectedUserTypes,
    });
  };

  const updateChannelTypes = selectedChannelTypes => {
    setChatFilters({
      chatType: MESSAGING_SECTIONS.CHANNELS,
      filters: { selectedChannelTypes },
    });
    recordMessagingFilters({
      value: selectedChannelTypes,
    });
  };

  const onClickChannel = useCallback(
    ({ key }) => {
      addNewChannel(key);
    },
    [addNewChannel]
  );

  const isGlobalHeaderRevampEnable = isMessagingGlobalHeaderRevampEnable();

  const renderFilterContainer = () => {
    switch (selectedSection) {
      case MESSAGING_SECTIONS.CHAT: {
        return (
          <div className={classes.initiateConversationContainer}>
            {isUserStaff && canShowStaffAndStudentChatToStaff() ? (
              <UserTypeFilter
                onOptionsSelected={setUserTypeFilters}
                values={selectedUserTypes}
              />
            ) : null}
            {showInitiateChat ? (
              <IconButton
                icon={<AddOutlined />}
                size="small"
                {...(isGlobalHeaderRevampEnable
                  ? {
                      style: styles.initiateButtonStyle,
                      shortcut: { code: "KeyC" },
                      dsVersion: "2.0",
                    }
                  : {})}
                onClick={addNewChat}
                variant={isGlobalHeaderRevampEnable ? "primary" : "progressive"}
              />
            ) : null}
          </div>
        );
      }
      case MESSAGING_SECTIONS.CHANNELS: {
        return (
          <div className={classes.initiateConversationContainer}>
            <ChannelTypeFilter
              value={selectedChannelTypes}
              onOptionsSelected={updateChannelTypes}
            />
            <StaffPermission>
              {canCreateChannel ? (
                <Dropdown
                  overlay={
                    <DropdownMenu
                      options={channelOptions}
                      onClick={onClickChannel}
                      dsVersion="2.0"
                      onShortcutPress={onClickChannel}
                    />
                  }
                  visible={createChannelDropdownOpen}
                  onVisibleChange={setCreateChannelDropdownOpen}
                  itemTitleContainerStyle={styles.itemTitleContainerStyle}
                >
                  <IconDisclosure
                    icon={<AddOutlined />}
                    {...(isGlobalHeaderRevampEnable
                      ? {
                          style: styles.createChannelIconDisclosureStyle,
                          shortcut: { code: "KeyC" },
                          dsVersion: "2.0",
                        }
                      : {})}
                    size="small"
                    variant={
                      isGlobalHeaderRevampEnable ? "primary" : "progressive"
                    }
                  />
                </Dropdown>
              ) : null}
            </StaffPermission>
          </div>
        );
      }
      default:
        return null;
    }
  };

  const showSearchBar = _.includes(
    [MESSAGING_SECTIONS.CHAT, MESSAGING_SECTIONS.CHANNELS],
    selectedSection
  );

  const getSearchBarPlaceHolder = () => {
    switch (selectedSection) {
      case MESSAGING_SECTIONS.CHAT:
        return t("common:fina_a_label", {
          label: t("common:chat"),
        });
      case MESSAGING_SECTIONS.CHANNELS:
        return t("common:fina_a_label", {
          label: t("common:channel"),
        });
      default:
        return null;
    }
  };

  return (
    <div className={classes.headerFilterContainer}>
      <div className={classes.headerTextContainer} dir="auto">
        <div className={threadHeaderClass}>
          {t(selectedSectionConfig.label)}
        </div>
        {renderFilterContainer()}
      </div>
      {showSearchBar ? (
        <div className={classes.searchBarContainer}>
          <SearchInput
            size="medium"
            placeholder={getSearchBarPlaceHolder()}
            onChange={setSearchText}
          />
        </div>
      ) : null}
    </div>
  );
};

const mapStateToProps = state => {
  const messagingState = getMessagingV2ReduxState(state);
  const selectedSectionV2 = _.get(state, "messagingV2.selectedSectionV2");

  return {
    selectedUserTypes: _.get(
      messagingState,
      [MESSAGING_SECTIONS.CHAT, "selectedUserTypes"],
      []
    ),
    selectedChannelTypes: _.get(
      messagingState,
      [MESSAGING_SECTIONS.CHANNELS, "selectedChannelTypes"],
      []
    ),
    createChannelDropdownOpen: _.get(
      messagingState,
      "createChannelDropdownOpen",
      false
    ),
    selectedSection: selectedSectionV2,
  };
};

const mapActionCreators = {
  setChatFilters,
  setCreateChannelDropdownOpen,
};

export default connect(
  mapStateToProps,
  mapActionCreators
)(HeaderRightContainer);
